/**
 * Chuẩn mapping message lỗi API → tiếng Việt (PHASE UX inline feedback).
 * Tất cả form phải dùng hàm này trước khi hiển thị submit/API error.
 */

const GENERIC_PATTERNS = [
  /Tham số không hợp lệ/i,
  /invalid parameter/i,
  /invalid request/i,
  /bad request/i,
  /dữ liệu không hợp lệ/i,
  /có lỗi xảy ra/i,
  /something went wrong/i,
  /internal server error/i,
];

const SYSTEM_MESSAGE = 'Không thể thực hiện. Vui lòng thử lại.';

export type ApiErrorKind = 'auth' | 'network' | 'server' | 'validation' | 'unknown';

export interface NormalizedApiErrorDetail {
  kind: ApiErrorKind;
  status?: number;
  code?: string;
  message: string;
}

/**
 * Chuẩn hóa message lỗi từ API thành câu tiếng Việt rõ ràng, có hành động.
 */
export function normalizeApiError(apiMessage?: string | null): string {
  const raw = typeof apiMessage === 'string' ? apiMessage.trim() : '';
  if (!raw) return SYSTEM_MESSAGE;
  const isGeneric = GENERIC_PATTERNS.some((p) => p.test(raw));
  if (isGeneric) return SYSTEM_MESSAGE;
  return raw;
}

/**
 * Normalize error object into a typed taxonomy used by session coordinator.
 * Keep normalizeApiError() intact for legacy message-only call sites.
 */
export function normalizeApiErrorDetail(errorOrMessage?: unknown): NormalizedApiErrorDetail {
  if (typeof errorOrMessage === 'string') {
    return {
      kind: 'unknown',
      message: normalizeApiError(errorOrMessage),
    };
  }

  if (errorOrMessage && typeof errorOrMessage === 'object') {
    const e = errorOrMessage as {
      kind?: string;
      status?: number;
      code?: string;
      message?: string;
      data?: { message?: string; code?: string };
      error?: { kind?: string; status?: number; code?: string; message?: string };
      response?: { status?: number; data?: { message?: string; code?: string } };
    };

    const status =
      e.error?.status ??
      e.status ??
      e.response?.status;

    const code =
      e.error?.code ??
      e.code ??
      e.data?.code ??
      e.response?.data?.code;

    const message =
      e.error?.message ??
      e.message ??
      e.data?.message ??
      e.response?.data?.message ??
      SYSTEM_MESSAGE;

    const explicitKind = e.error?.kind ?? e.kind;
    if (
      explicitKind === 'auth' ||
      explicitKind === 'network' ||
      explicitKind === 'server' ||
      explicitKind === 'validation' ||
      explicitKind === 'unknown'
    ) {
      return { kind: explicitKind, status, code, message: normalizeApiError(message) };
    }

    const lowerMessage = String(message).toLowerCase();
    const isNetworkMessage =
      lowerMessage.includes('failed to fetch') ||
      lowerMessage.includes('networkerror') ||
      lowerMessage.includes('network error') ||
      lowerMessage.includes('err_failed') ||
      lowerMessage.includes('cors') ||
      lowerMessage.includes('timeout') ||
      lowerMessage.includes('aborterror');

    if (status === 401 || status === 403) {
      return { kind: 'auth', status, code, message: normalizeApiError(message) };
    }
    if (status === 0 || isNetworkMessage) {
      return { kind: 'network', status, code, message: normalizeApiError(message) };
    }
    if (typeof status === 'number' && status >= 500) {
      return { kind: 'server', status, code, message: normalizeApiError(message) };
    }
    if (typeof status === 'number' && (status === 400 || status === 422)) {
      return { kind: 'validation', status, code, message: normalizeApiError(message) };
    }

    return { kind: 'unknown', status, code, message: normalizeApiError(message) };
  }

  return { kind: 'unknown', message: SYSTEM_MESSAGE };
}
